import React from "react";
import {
	ChildrenProps,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	If,
	makeClassName,
	Row,
	StyleProps,
} from "@hex-insights/core";
import { Icon, IconContainer } from "../Icon";
import styles from "./styles.module.css";

type EmptyViewProps = {
	IconComponent: (props: Icon.IconProps) => JSX.Element | null;
	iconSize?: number;
	primaryText: React.ReactNode;
	secondaryText: React.ReactNode;
	layout?: "row" | "column";
} & Partial<ChildrenProps & ClassNameProps & StyleProps>;

export function EmptyView({
	IconComponent,
	iconSize: iconSizeProp,
	primaryText,
	secondaryText,
	layout = "column",
	children,
	className,
	style,
}: EmptyViewProps) {
	const iconSize = iconSizeProp ?? (layout === "row" ? 40 : 74);

	const content = (
		<React.Fragment>
			<IconContainer
				color="var(--neutral-colors--light)"
				className={styles["empty__icon-container"]}
				style={{ "--icon-size": iconSize } as React.CSSProperties}
			>
				<IconComponent color="var(--neutral-colors--secondary)" size={iconSize} />
			</IconContainer>

			<Column justify="spaced-start" align={layout === "row" ? undefined : "center"} verticalSpacing="0.5rem">
				<span className={styles["empty__text--primary"]}>{primaryText}</span>
				<span className={styles["empty__text--secondary"]}>{secondaryText}</span>
				{children}
			</Column>
		</React.Fragment>
	);

	return (
		<EmptyView.Container className={className} style={style}>
			<Conditional>
				<If condition={layout === "row"}>
					<Row justify="spaced-start" align="center" horizontalSpacing="1rem">
						{content}
					</Row>
				</If>
				<Else>
					<Column justify="spaced-start" align="center" verticalSpacing="1rem">
						{content}
					</Column>
				</Else>
			</Conditional>
		</EmptyView.Container>
	);
}

export type EmptyViewContainerProps = Partial<ClassNameProps & StyleProps & ChildrenProps>;

EmptyView.Container = function ({ className, style, children }: EmptyViewContainerProps) {
	return (
		<Column
			justify="center"
			align="center"
			className={makeClassName(styles["empty__container"], className)}
			style={style}
		>
			{children}
		</Column>
	);
};

type SlimEmptyViewProps = Partial<ClassNameProps & StyleProps & ChildrenProps>;

export function SlimEmptyView({ className, style, children }: SlimEmptyViewProps) {
	return (
		<Row
			justify="center"
			align="center"
			verticalSpacing="0.5rem"
			className={makeClassName(styles["empty--slim"], className)}
			style={style}
		>
			{children}
		</Row>
	);
}
